import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Grid, TextField, Button, Alert, Divider, Skeleton,
  Select, MenuItem,
} from '@mui/material';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

const MODE_INFO = {
  auto: {
    label: 'Auto (Google Play)',
    color: '#3182CE',
    blurb: 'The app defers to Google Play In-App Updates. Play detects newer builds and shows its own flexible update flow. The min/latest numbers below are ignored in this mode.',
  },
  soft: {
    label: 'Soft (optional prompt)',
    color: '#D69E2E',
    blurb: 'The app shows a DISMISSABLE "Update available" prompt when the installed build number is below "Latest build". Users can still continue.',
  },
  force: {
    label: 'Force (required update)',
    color: '#C53030',
    blurb: 'The app shows a NON-DISMISSABLE "Update required" gate when the installed build number is below "Minimum supported build". Users cannot proceed until they update.',
  },
};

const Field = ({ label, children, help }) => (
  <Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 600, color: '#4A5568', mb: 0.5 }}>{label}</Box>
    {children}
    {help && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 0.5 }}>{help}</Box>}
  </Box>
);

const PlatformBlock = ({ title, data, mode, onChange }) => (
  <Paper sx={{ p: 3, borderRadius: '16px', mb: 2 }}>
    <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 700, color: '#1A202C', mb: 2 }}>{title}</Box>
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6}>
        <Field label="Minimum supported build" help="Force-gate below this (versionCode). Used in Force mode.">
          <TextField fullWidth size="small" type="number" value={data.minSupported ?? 0}
            onChange={(e) => onChange({ minSupported: e.target.value })}
            disabled={mode !== 'force'} />
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field label="Latest build" help="Prompt below this (versionCode). Used in Soft mode.">
          <TextField fullWidth size="small" type="number" value={data.latest ?? 0}
            onChange={(e) => onChange({ latest: e.target.value })}
            disabled={mode !== 'soft'} />
        </Field>
      </Grid>
      <Grid item xs={12}>
        <Field label="Store URL" help="Where the Update button sends the user.">
          <TextField fullWidth size="small" value={data.storeUrl || ''}
            onChange={(e) => onChange({ storeUrl: e.target.value })}
            placeholder="market://details?id=com.oktreat.app" />
        </Field>
      </Grid>
    </Grid>
  </Paper>
);

export default function AppVersionSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/app-version');
        setS((res.data.data || res.data).settings);
      } catch (e) { setError('Failed to load settings.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (patch) => { setS((prev) => ({ ...prev, ...patch })); setSaved(false); };
  const setPlatform = (key, patch) =>
    setS((prev) => { setSaved(false); return { ...prev, [key]: { ...prev[key], ...patch } }; });

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.put('/admin/app-version', s);
      setS((res.data.data || res.data).settings);
      setSaved(true);
    } catch (e) { setError(e.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading || !s) {
    return <Box sx={{ p: 1 }}><Skeleton variant="rounded" height={420} /></Box>;
  }

  const info = MODE_INFO[s.mode] || MODE_INFO.auto;

  return (
    <Box sx={{ fontFamily: ff, maxWidth: 860 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Settings saved.</Alert>}

      <Paper sx={{ p: 3, borderRadius: '16px', mb: 2 }}>
        <Field label="Update mode">
          <Select fullWidth size="small" value={s.mode} onChange={(e) => set({ mode: e.target.value })}>
            {Object.entries(MODE_INFO).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontFamily: ff }}>{v.label}</MenuItem>
            ))}
          </Select>
        </Field>
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#F7FAFC',
          borderLeft: `4px solid ${info.color}` }}>
          <Box sx={{ fontFamily: ff, fontSize: 12.5, color: '#4A5568' }}>{info.blurb}</Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Field label="Update message" help="Shown in the soft prompt / force screen.">
          <TextField fullWidth size="small" multiline minRows={2} value={s.message || ''}
            onChange={(e) => set({ message: e.target.value })} />
        </Field>
      </Paper>

      <PlatformBlock title="Android" data={s.android || {}} mode={s.mode}
        onChange={(patch) => setPlatform('android', patch)} />
      <PlatformBlock title="iOS" data={s.ios || {}} mode={s.mode}
        onChange={(patch) => setPlatform('ios', patch)} />

      <Alert severity="info" sx={{ mb: 2, fontFamily: ff }}>
        Before raising the minimum in Force mode, make sure the newer build is already live on the
        store/testing track — otherwise users have nowhere to update to.
      </Alert>

      <Button variant="contained" onClick={save} disabled={saving}
        sx={{ fontFamily: ff, textTransform: 'none', bgcolor: '#32A6D8', px: 4 }}>
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </Box>
  );
}
